import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import { macroCondition, isDevelopingApp } from '@embroider/macros';
import { queue } from '@nullvoxpopuli/ember-composable-helpers';
import set from 'ember-set-helper/helpers/set';
import {
  HdsPageHeader,
  HdsButton,
  HdsModal,
  HdsButtonSet,
  HdsSeparator,
  HdsBadge,
} from '@hashicorp/design-system-components/components';
import {
  FlexGrid,
  Typography,
  BreadcrumbsContainer,
  CoreLayoutMain,
} from 'core/components';
import { boxMargin, boxPadding } from 'core/modifiers';
import { optionForSourceState } from 'core/helpers';
import { GEOS } from 'core/utils/constants';
import AlertBanner from 'hcp/components/page/account-settings/security/alert-banner';
import Enable from 'hcp/components/manage-mfa/enable';
import Disable from 'hcp/components/manage-mfa/disable';

/**
 *
 * `PageAccountSettingsSecurity` renders the user's password and MFA reset functionality.
 *
 *
 * ```
 * <Page::AccountSettings::Security />
 * ```
 *
 * @class PageAccountSettingsSecurity
 *
 */
export default class PageAccountSettingsSecurity extends Component {
  @service config;
  @service currentUser;
  @service api;
  @service intl;
  @service flashMessages;

  // Modal state
  @tracked showPasswordResetModal = false;
  @tracked showManageMFAModal = false;

  REGION_US = GEOS.US;

  resetUserPassword = task(async () => {
    try {
      await this.api.profile.profileServiceResetPassword();
      this.flashMessages.success(
        this.intl.t(
          'components.page.account-settings.security.passReset.flashMessage.success',
          {
            htmlSafe: true,
            userEmail: this.currentUser.user.email,
          }
        )
      );
    } catch (e) {
      if (macroCondition(isDevelopingApp())) {
        console.error(e);
      }
      this.flashMessages.error(
        this.intl.t(
          'components.page.account-settings.security.passReset.flashMessage.error'
        )
      );
    }
    this.showPasswordResetModal = false;
  });

  get MFAEnabled() {
    let { mfaStatus } = this.args.model;

    if (mfaStatus === 'ENABLED') {
      return true;
    } else {
      return false;
    }
  }

  get deployGeo() {
    return this.config?.app?.deployGeo ?? GEOS.US;
  }
  <template>
    <CoreLayoutMain>
      <:header>
        <HdsPageHeader as |PH|>
          <PH.Breadcrumb>
            <BreadcrumbsContainer />
          </PH.Breadcrumb>
          <PH.Title>
            {{t "components.page.account-settings.nav.security"}}
          </PH.Title>
          <PH.IconTile @icon="lock" />
        </HdsPageHeader>
      </:header>
      <:body>
        <div class="accountSettings">
          {{#let this.currentUser.user as |user|}}
            <FlexGrid as |FG|>
              <FG.Item @xs="12" @lg="7" />
            </FlexGrid>

            {{! PASSWORD RESET START}}
            <section class="accountSettings__passwordReset">
              <Typography @variant="h2">
                {{t
                  "components.page.account-settings.security.passReset.heading"
                }}
              </Typography>
              <Typography @variant="body1">
                {{t "components.page.account-settings.security.passReset.body"}}
              </Typography>
              <HdsButton
                @color="secondary"
                @text={{t
                  "components.page.account-settings.security.passReset.button"
                }}
                id="password-reset-modal-open"
                data-test-password-reset-showModal
                {{on "click" (set this "showPasswordResetModal" true)}}
              />
              {{! TODO: https://hashicorp.atlassian.net/browse/HCPIE-318
            Refactor Password Reset Modal into it's own component
          }}
              {{#if this.showPasswordResetModal}}
                <HdsModal
                  @returnFocusTo="password-reset-modal-open"
                  @onClose={{set this "showPasswordResetModal" false}}
                  class="accountSettings__modal"
                  data-test-modal-dialog-container
                  as |MD|
                >
                  <MD.Header @icon="info">
                    {{t
                      "components.page.account-settings.security.passReset.modal.header"
                    }}
                  </MD.Header>
                  <MD.Body data-test-modal-dialog-body>
                    {{#if (eq this.deployGeo this.REGION_US)}}
                      <AlertBanner
                        data-test-password-reset-warning
                        @header={{t
                          "components.page.account-settings.security.banner.heading.passReset"
                        }}
                        {{boxMargin "0 0 md 0"}}
                      />
                    {{/if}}
                    {{t
                      "components.page.account-settings.security.passReset.modal.body1"
                    }}
                    {{user.email}}.
                    {{t
                      "components.page.account-settings.security.passReset.modal.body2"
                    }}
                  </MD.Body>
                  <MD.Footer as |F|>
                    <HdsButtonSet>
                      <HdsButton
                        @color="primary"
                        @text={{t
                          "components.page.account-settings.security.passReset.modal.sendEmailBtn"
                        }}
                        data-test-password-reset
                        {{on "click" this.resetUserPassword.perform}}
                      />
                      <HdsButton
                        type="button"
                        @text={{t "components.form.cancel"}}
                        @color="secondary"
                        {{on "click" F.close}}
                      />
                    </HdsButtonSet>
                  </MD.Footer>
                </HdsModal>
              {{/if}}
            </section>
            {{! PASSWORD RESET END}}

            {{! MFA START}}
            <section>
              <HdsSeparator {{boxMargin "xl 0"}} />
              <Typography @variant="h2">
                {{t "components.page.account-settings.security.mfa.heading"}}
              </Typography>
              <Typography @variant="body1">
                {{t "components.page.account-settings.security.mfa.body"}}
              </Typography>
              <dl class="stacked" {{boxPadding "md 0"}}>
                <dt>
                  {{t
                    "components.page.account-settings.security.mfa.status.title"
                  }}
                </dt>
                <dd>
                  {{#let
                    (optionForSourceState
                      "mfa" (if this.MFAEnabled "enabled" "disabled")
                    )
                    as |option|
                  }}
                    <HdsBadge
                      @text={{t
                        (concat
                          "components.page.account-settings.security.mfa.status."
                          (if this.MFAEnabled "enabled" "disabled")
                        )
                      }}
                      @color={{option.color}}
                      @icon={{option.icon}}
                      @isIconOnly={{option.isIconOnly}}
                      @size={{option.size}}
                      @type={{option.type}}
                    />
                  {{/let}}
                </dd>
              </dl>
              <HdsButton
                data-test-enable-disable-mfa
                id="manage-mfa"
                @color={{if this.MFAEnabled "critical" "secondary"}}
                @text={{t
                  (if
                    this.MFAEnabled
                    "components.page.account-settings.security.mfaDisable.heading"
                    "components.page.account-settings.security.mfaModal.step1.heading"
                  )
                }}
                {{on "click" (set this "showManageMFAModal" true)}}
              />
            </section>
          {{/let}}
        </div>
        {{#if this.showManageMFAModal}}
          {{#if (eq @model.mfaStatus "DISABLED")}}
            <Enable
              data-test-mfa-enable-modal
              @onStatusChange={{queue
                (set this "showManageMFAModal" false)
                @model.refresh
              }}
              @onCancel={{set this "showManageMFAModal" false}}
            />
          {{else if (eq @model.mfaStatus "ENABLED")}}
            <Disable
              data-test-mfa-disable-modal
              @onStatusChange={{queue
                (set this "showManageMFAModal" false)
                @model.refresh
              }}
              @onCancel={{set this "showManageMFAModal" false}}
            />
          {{/if}}
        {{/if}}
        {{! MFA END}}
      </:body>
    </CoreLayoutMain>
  </template>
}
